import { useMemo } from 'react';
import { Autocomplete, TextField, Chip, Box, createFilterOptions } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { tagsService, Tag, contrastText } from '../services/tags.service';

const filter = createFilterOptions<Tag>();

interface TagSelectProps {
  value: string[];                       // selected tag ids
  onChange: (ids: string[]) => void;
  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  allowCreate?: boolean;                 // type a new name to create a tag (forms); off for filters
  sx?: object;
}

/**
 * Reusable multi-select for tags. Renders colored chips; when allowCreate is on,
 * typing a new name and pressing Enter creates the tag (server-side idempotent).
 */
export default function TagSelect({
  value,
  onChange,
  label = 'Tags',
  placeholder,
  size = 'small',
  allowCreate = true,
  sx,
}: TagSelectProps) {
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ['tags'], queryFn: () => tagsService.query() });
  const tags = data?.data ?? [];

  const selected = useMemo(() => value.map((id) => tags.find((t) => t.id === id)).filter(Boolean) as Tag[], [value, tags]);

  const handleChange = async (newValue: (Tag | string)[]) => {
    // Excludes the synthetic "Add …" option — its id is a placeholder, not a real tag id.
    const existing = newValue.filter(
      (v): v is Tag => typeof v !== 'string' && !(v as Tag & { inputValue?: string }).inputValue,
    );
    // Free-text entries (allowCreate) and the synthetic "Add …" option carry a name to create.
    const names = newValue
      .map((v) => (typeof v === 'string' ? v : (v as Tag & { inputValue?: string }).inputValue))
      .filter((n): n is string => !!n);

    let created: Tag[] = [];
    if (names.length) {
      created = await Promise.all(names.map((name) => tagsService.create({ name })));
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    }
    // De-dupe by id (a freshly created tag may already exist)
    const ids = Array.from(new Set([...existing, ...created].map((t) => t.id)));
    onChange(ids);
  };

  return (
    <Autocomplete
      multiple
      size={size}
      sx={sx}
      options={tags}
      value={selected}
      disableCloseOnSelect
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onChange={(_, v) => handleChange(v as (Tag | string)[])}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);
        const input = params.inputValue.trim();
        if (allowCreate && input && !options.some((o) => o.name.toLowerCase() === input.toLowerCase())) {
          filtered.push({ id: `__create__${input}`, name: `Add "${input}"`, inputValue: input, color: '#607d8b' } as Tag & { inputValue: string });
        }
        return filtered;
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: option.color, mr: 1, flexShrink: 0 }} />
          {option.name}
        </li>
      )}
      renderTags={(vals, getTagProps) =>
        vals.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={option.name}
            size="small"
            sx={{ bgcolor: option.color, color: contrastText(option.color), fontWeight: 500 }}
          />
        ))
      }
      renderInput={(params) => <TextField {...params} label={label} placeholder={placeholder} size={size} />}
    />
  );
}
